'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { createGame, tick, typeChar, TYPING_CHALLENGE_ROUND_MS } from '@/features/games/typing-challenge/logic';
import { recordGameAttempt, getGameRecord } from '@/lib/storage/games';
import { appendAttempt } from '@/lib/storage/attempts';
import { useGameLoop } from '@/hooks/use-game-loop';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export function TypingChallengeGame({ defaultDifficulty = 'medium' }) {
  const [difficulty, setDifficulty] = useState(defaultDifficulty);
  const [seed, setSeed] = useState(1);
  const [state, setState] = useState(() => createGame({ seed: 1, difficulty: defaultDifficulty }));
  const [record, setRecord] = useState(null);
  const inputRef = useRef(null);

  useGameLoop({
    phase: state.phase,
    tick: (nowMs) => setState((s) => (s.phase === 'running' ? tick(s, nowMs) : s)),
  });

  useEffect(() => { setRecord(getGameRecord(`typing-challenge:${difficulty}`)); }, [difficulty]);

  useEffect(() => {
    if (state.phase === 'ended' && state.startedAtMs > 0) {
      const elapsedSec = Math.max(1, state.elapsedMs / 1000);
      const wpm = (state.correctChars * 60) / (5 * elapsedSec);
      setRecord(recordGameAttempt({ gameId: `typing-challenge:${difficulty}`, score: state.score, wpm }));
      appendAttempt({
        activityType: 'game',
        activityRefSlug: `typing-challenge:${difficulty}`,
        durationSeconds: Math.round(elapsedSec),
        grossWpm: Math.round(wpm),
        netWpm: Math.round(wpm),
        accuracy: state.correctChars + state.incorrectChars > 0
          ? (state.correctChars / (state.correctChars + state.incorrectChars)) * 100
          : 100,
        correctChars: state.correctChars,
        incorrectChars: state.incorrectChars,
        totalChars: state.correctChars + state.incorrectChars,
        backspaces: 0,
      });
    }
  }, [state.phase, state.score, state.correctChars, state.incorrectChars, state.elapsedMs, state.startedAtMs, difficulty]);

  useEffect(() => { if (state.phase === 'running') inputRef.current?.focus(); }, [state.phase]);

  const reset = useCallback((d = difficulty) => {
    setSeed((s) => s + 1);
    setState(createGame({ seed: seed + 1, difficulty: d }));
  }, [seed, difficulty]);

  const onKeyDown = (e) => {
    if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
    e.preventDefault();
    setState((s) => typeChar(s, e.key));
  };

  const remaining = state.startedAtMs === 0
    ? Math.round(TYPING_CHALLENGE_ROUND_MS / 1000)
    : Math.max(0, Math.ceil((TYPING_CHALLENGE_ROUND_MS - state.elapsedMs) / 1000));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1">
          {['easy', 'medium', 'hard'].map((d) => (
            <Button key={d} size="sm" variant={difficulty === d ? 'primary' : 'secondary'}
              onClick={() => { setDifficulty(d); reset(d); }}
              disabled={state.phase === 'running'}>{d}</Button>
          ))}
        </div>
        <div className="flex items-center gap-4 text-sm tabular-nums">
          <span className="text-text-secondary">Score <strong className="text-text-primary">{state.score}</strong></span>
          <span className="text-text-secondary">Time <strong className="text-text-primary">{remaining}s</strong></span>
          <span className="text-text-secondary">Words <strong className="text-text-primary">{state.correctWords}</strong></span>
        </div>
      </div>

      <Card>
        <p className="text-center font-mono text-2xl tracking-wider">
          {state.prompt.text.split('').map((ch, i) => (
            <span key={i} className={i < state.prompt.position ? 'text-text-secondary' : 'text-text-primary'}>
              {ch}
            </span>
          ))}
        </p>
      </Card>

      <input ref={inputRef} className="sr-only" aria-label="Typing Challenge input" value="" readOnly
        onKeyDown={onKeyDown} tabIndex={state.phase === 'running' ? 0 : -1} />

      {state.phase === 'ended' && (
        <Card title="Round complete">
          <p className="text-sm">
            Score <strong className="tabular-nums text-text-primary">{state.score}</strong> ·{' '}
            {state.correctWords} words ·{' '}
            {state.correctChars + state.incorrectChars > 0
              ? Math.round((state.correctChars / (state.correctChars + state.incorrectChars)) * 100)
              : 100}% accuracy
            {record && <> · Best <strong className="tabular-nums">{record.bestScore}</strong></>}
          </p>
          <div className="mt-4"><Button onClick={() => reset()}>Play again</Button></div>
        </Card>
      )}

      {state.phase === 'running' && (
        <p className="text-center text-sm text-text-muted">Press any letter key to start typing. The round lasts 60 seconds.</p>
      )}
    </div>
  );
}